<template>
  <div class="list-view">
    <div class="resource-tabs">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'vms' }"
        @click="activeTab = 'vms'"
      >
        {{ t('stats.virtualMachines') }} ({{ resources.vms.length }})
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'disks' }"
        @click="activeTab = 'disks'"
      >
        {{ t('stats.disks') }} ({{ resources.disks.length }})
      </button>
    </div>

    <!-- VM列表 -->
    <div v-if="activeTab === 'vms'" class="resource-list">
      <div v-if="!resources.vms.length" class="empty-state">
        <i class="fas fa-server"></i>
        <p>{{ t('vm.noVMs') }}</p>
      </div>

      <div
        v-for="vm in resources.vms"
        :key="vm.name"
        class="resource-card"
        :class="{ 'on-premise': vm.isOnPremise }"
      >
        <div class="card-header">
          <h3 class="card-title">
            <i :class="vm.isOnPremise ? 'fas fa-desktop' : 'fas fa-server'" class="vm-icon"></i>
            {{ vm.name }}
            <span v-if="vm.isOnPremise" class="on-premise-badge">{{ t('vm.onPremise') }}</span>
          </h3>
          <span class="card-status" :class="getVMStatusClass(vm)">
            {{ getVMStatus(vm) }}
          </span>
        </div>

        <div class="card-details">
          <div class="detail-item">
            <span class="detail-label">{{ t('vm.zone') }}</span>
            <span class="detail-value">{{ vm.zone }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('vm.machineType') }}</span>
            <span class="detail-value">{{ getMachineType(vm) }}</span>
          </div>
          <div v-if="vm.isOnPremise && vm.platformName" class="detail-item">
            <span class="detail-label">Platform</span>
            <span class="detail-value">{{ vm.platformName }} {{ vm.platformVersion }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('vm.internalIP') }}</span>
            <span class="detail-value">{{ getInternalIP(vm) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('vm.externalIP') }}</span>
            <span class="detail-value">{{ getExternalIP(vm) }}</span>
          </div>

          <!-- 标签显示 -->
          <div class="detail-item">
            <span class="detail-label">{{ t('labels.title') }}</span>
            <div class="labels-section">
              <div v-if="hasLabels(vm)" class="labels-display">
                <span
                  v-if="vm.labels?.owner"
                  class="label-tag owner"
                  :title="`Owner: ${formatOwnerForDisplay(vm.labels.owner)}`"
                >
                  <i class="fas fa-user"></i>
                  {{ formatOwnerForDisplay(vm.labels.owner).split('@')[0] }}
                </span>
                <span
                  v-if="vm.labels?.team"
                  class="label-tag team"
                  :title="`Team: ${getTeamDisplayName(vm.labels.team)}`"
                >
                  <i class="fas fa-users"></i>
                  {{ getTeamDisplayName(vm.labels.team) }}
                </span>
                <span
                  v-if="vm.labels?.org"
                  class="label-tag org"
                  :title="`Org: ${getOrgDisplayName(vm.labels.org)}`"
                >
                  <i class="fas fa-building"></i>
                  {{ getOrgDisplayName(vm.labels.org) }}
                </span>
              </div>
              <div v-else class="no-labels">
                <span class="no-labels-text">{{ t('labels.noLabels') }}</span>
              </div>
                <button
                  v-if="user && user.role !== 'viewer' && !vm.isOnPremise"
                  @click="openLabelModal('vm', vm)"
                  class="btn btn-outline btn-sm label-edit-btn"
                >
                  <i class="fas fa-edit"></i>
                  {{ t('common.edit') }}
                </button>
            </div>
          </div>

          <!-- SSH 命令区域 -->
          <div class="ssh-section">
            <div class="detail-item">
              <span class="detail-label">{{ t('vm.sshCommand') }}</span>
              <div class="ssh-command-wrapper">
                <el-input
                  :model-value="getSshCommand(vm)"
                  readonly
                  size="small"
                  class="ssh-command-input"
                >
                  <template #append>
                    <el-button
                      size="small"
                      @click="copySshCommand(vm)"
                      :disabled="!getSshCommand(vm)"
                    >
                      {{ t('common.copy') }}
                    </el-button>
                  </template>
                </el-input>
                <div class="ssh-note">
                  <small v-if="vm.isOnPremise">
                    <i class="fas fa-info-circle"></i> {{ t('vm.ssmSessionManager') }}
                  </small>
                  <small v-else-if="hasExternalIP(vm)">
                    <i class="fas fa-info-circle"></i> {{ t('vm.hasExternalIP') }}
                  </small>
                  <small v-else>
                    <i class="fas fa-info-circle"></i> {{ t('vm.noExternalIP') }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-actions">
          <!-- Hide start/stop buttons for on-premise instances -->
          <button
            v-if="user && user.role !== 'viewer' && (vm.status === 'RUNNING' || vm.status === 'running') && !vm.isOnPremise"
            @click="$emit('vm-operation', 'stop', vm.name, vm.zone)"
            class="btn btn-warning btn-sm"
          >
            <i class="fas fa-pause"></i> {{ t('vm.stopVM') }}
          </button>
          <button
            v-if="user && user.role !== 'viewer' && vm.status !== 'RUNNING' && vm.status !== 'running' && !vm.isOnPremise"
            @click="$emit('vm-operation', 'start', vm.name, vm.zone)"
            class="btn btn-success btn-sm"
          >
            <i class="fas fa-play"></i> {{ t('vm.startVM') }}
          </button>
          <!-- Hide delete button for on-premise instances -->
          <button
            v-if="user && user.role === 'admin' && !vm.isOnPremise"
            @click="$emit('vm-operation', 'delete', vm.name, vm.zone)"
            class="btn btn-danger btn-sm"
          >
            <i class="fas fa-trash"></i> {{ t('vm.deleteVM') }}
          </button>
          <InfoPopoverBtn :type="'vm'" :item="vm" :currentProjectId="currentProjectId" />
        </div>
      </div>
    </div>

    <!-- 磁盘列表 -->
    <div v-if="activeTab === 'disks'" class="resource-list">
      <div v-if="!resources.disks.length" class="empty-state">
        <i class="fas fa-hdd"></i>
        <p>{{ t('disk.noDisks') }}</p>
      </div>

      <div
        v-for="disk in resources.disks"
        :key="disk.name"
        class="resource-card"
      >
        <div class="card-header">
          <h3 class="card-title">{{ disk.name }}</h3>
          <span class="card-status" :class="getDiskStatusClass(disk)">
            {{ getDiskStatus(disk) }}
          </span>
        </div>

        <div class="card-details">
          <div class="detail-item">
            <span class="detail-label">{{ t('disk.zone') }}</span>
            <span class="detail-value">{{ disk.zone }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('disk.size') }}</span>
            <span class="detail-value">{{ (disk.sizeGb || disk.size) }} GB</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('disk.type') }}</span>
            <span class="detail-value">{{ getDiskType(disk) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('disk.status') }}</span>
            <span class="detail-value">{{ disk.status }}</span>
          </div>

          <!-- 挂载信息 -->
          <div v-if="isDiskAttached(disk)" class="detail-item">
            <span class="detail-label">{{ t('disk.attachedTo') }}</span>
            <span class="detail-value">
              <i class="fas fa-link"></i>
              {{ getAttachedVMName(disk) }}
            </span>
          </div>
          <div v-if="isDiskAttached(disk)" class="detail-item">
            <span class="detail-label">{{ t('disk.device') }}</span>
            <span class="detail-value">
              <i class="fas fa-hdd"></i>
              {{ getAttachedDevice(disk) }}
            </span>
          </div>
          <div v-if="!isDiskAttached(disk)" class="detail-item">
            <span class="detail-label">{{ t('disk.attachedTo') }}</span>
            <span class="detail-value status-unattached">
              <i class="fas fa-unlink"></i>
              {{ t('disk.notAttached') }}
            </span>
          </div>

          <!-- 标签显示 -->
          <div class="detail-item">
            <span class="detail-label">{{ t('labels.title') }}</span>
            <div class="labels-section">
              <div v-if="hasLabels(disk)" class="labels-display">
                <span
                  v-if="disk.labels?.owner"
                  class="label-tag owner"
                  :title="`Owner: ${formatOwnerForDisplay(disk.labels.owner)}`"
                >
                  <i class="fas fa-user"></i>
                  {{ formatOwnerForDisplay(disk.labels.owner).split('@')[0] }}
                </span>
                <span
                  v-if="disk.labels?.team"
                  class="label-tag team"
                  :title="`Team: ${getTeamDisplayName(disk.labels.team)}`"
                >
                  <i class="fas fa-users"></i>
                  {{ getTeamDisplayName(disk.labels.team) }}
                </span>
                <span
                  v-if="disk.labels?.org"
                  class="label-tag org"
                  :title="`Org: ${getOrgDisplayName(disk.labels.org)}`"
                >
                  <i class="fas fa-building"></i>
                  {{ getOrgDisplayName(disk.labels.org) }}
                </span>
              </div>
              <div v-else class="no-labels">
                <span class="no-labels-text">{{ t('labels.noLabels') }}</span>
              </div>
              <button
                v-if="user && user.role !== 'viewer'"
                @click="openLabelModal('disk', disk)"
                class="btn btn-outline btn-sm label-edit-btn"
              >
                <i class="fas fa-edit"></i>
                {{ t('common.edit') }}
              </button>
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button
            v-if="user && user.role === 'admin'"
            @click="$emit('delete-disk', currentProvider === 'aws' ? disk.id : disk.name, disk.zone)"
            class="btn btn-danger btn-sm"
          >
            <i class="fas fa-trash"></i> {{ t('common.delete') }}
          </button>
          <InfoPopoverBtn :type="'disk'" :item="disk" :currentProjectId="currentProjectId" />
        </div>
      </div>
    </div>

    <!-- 标签编辑模态框 -->
    <LabelEditModal
      v-if="labelModalVisible"
      :visible="labelModalVisible"
      :resourceType="labelModalData.resourceType"
      :resourceName="labelModalData.resourceName"
      :zone="labelModalData.zone"
      :currentLabels="labelModalData.currentLabels"
      :projectId="currentProjectId"
      @close="closeLabelModal"
      @labels-updated="handleLabelsUpdated"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { ElMessage } from 'element-plus';
import InfoPopoverBtn from './InfoPopoverBtn.vue';
import LabelEditModal from './LabelEditModal.vue';
import { useI18n } from 'vue-i18n';
import { useCloudProvider } from '../composables/useCloudProvider.js';

const { t } = useI18n();
const { currentProvider } = useCloudProvider();
const activeTab = ref('vms');

// 标签编辑模态框状态
const labelModalVisible = ref(false);
const labelModalData = ref({
  resourceType: '',
  resourceName: '',
  zone: '',
  currentLabels: {}
});

// 获取VM状态
function getVMStatus(vm) {
  if (vm && (vm.status === 'RUNNING' || vm.status === 'running')) {return t('vm.status.running');}
  if (vm && vm.status === 'STOPPED') {return t('vm.status.stopped');}
  if (vm && vm.status === 'TERMINATED') {return t('vm.status.terminated');}
  if (vm && vm.status === 'SUSPENDED') {return t('vm.status.suspended');}
  if (vm && vm.status === 'SUSPENDING') {return t('vm.status.suspending');}
  if (vm && vm.status === 'STOPPING') {return t('vm.status.stopping');}
  return (vm && vm.status) ? vm.status : t('vm.status.unknown');
}

// 获取VM状态样式类
const getVMStatusClass = (vm) => {
  const status = vm && vm.status?.toLowerCase();
  if (status === 'running') {return 'status-running';}
  if (status === 'stopped') {return 'status-stopped';}
  if (status === 'terminated') {return 'status-terminated';}
  return '';
};

// 获取磁盘状态
const getDiskStatus = (disk) => {
  if (disk && disk.status === 'READY') {return t('disk.status.ready');}
  if (disk && disk.status === 'CREATING') {return t('disk.status.creating');}
  if (disk && disk.status === 'RESTORING') {return t('disk.status.restoring');}
  return disk && disk.status || t('disk.status.unknown');
};

// 获取磁盘状态样式类
const getDiskStatusClass = (disk) => {
  const status = disk && disk.status?.toLowerCase();
  if (status === 'ready') {return 'status-ready';}
  if (status === 'creating') {return 'status-creating';}
  if (status === 'restoring') {return 'status-restoring';}
  return '';
};

// 获取机器类型
const getMachineType = (vm) => {
  if (currentProvider.value === 'aws') {
    return vm.instanceType ?? t('vm.na');
  } else {
    return vm.machineType?.split('/').pop() ?? t('vm.na');
  }
};

// 获取内部IP
const getInternalIP = (vm) => {
  if (currentProvider.value === 'aws') {
    return vm?.privateIP ?? t('vm.na');
  } else {
    return vm?.networkInterfaces?.[0]?.networkIP ?? t('vm.na');
  }
};

// 获取外部IP
const getExternalIP = (vm) => {
  if (currentProvider.value === 'aws') {
    return vm?.publicIP ?? t('vm.na');
  } else {
    return vm?.networkInterfaces?.[0]?.accessConfigs?.[0]?.natIP ?? t('vm.na');
  }
};

// 获取磁盘类型
const getDiskType = (disk) => {
  return disk && disk.type?.split('/').pop() || t('disk.na');
};

// 获取磁盘挂载的VM名称
const getAttachedVMName = (disk) => {
  if (!disk.attachedTo || disk.attachedTo.length === 0) {
    return t('disk.notAttached');
  }

  if (currentProvider.value === 'aws') {
    // AWS: 从attachedTo中获取实例ID，然后从VM列表中查找对应的名称
    const attachment = disk.attachedTo[0];
    const vm = props.resources.vms.find(vm => vm.id === attachment.instanceId);
    return vm ? vm.name : attachment.instanceId;
  } else {
    // GCP: 从attachedTo中获取实例名称
    return disk.attachedTo[0].instanceName;
  }
};

// 获取磁盘挂载的设备名
const getAttachedDevice = (disk) => {
  if (!disk.attachedTo || disk.attachedTo.length === 0) {
    return t('disk.na');
  }

  return disk.attachedTo[0].device || t('disk.na');
};

// 判断磁盘是否已挂载
const isDiskAttached = (disk) => {
  return disk.attachedTo && disk.attachedTo.length > 0;
};

const getSshCommand = (vm) => {
  if (currentProvider.value === 'aws') {
    if (vm.isOnPremise) {
      // AWS SSM Session Manager for on-premise instances
      let ssmCommand = `aws ssm start-session --target ${vm.id} --region ${vm.region}`;
      
      // 检查是否有 Document 标签，如果有则添加 --document 参数
      if (vm.labels && vm.labels.Document) {
        ssmCommand += ` --document ${vm.labels.Document}`;
      }
      
      return ssmCommand;
    } else {
      // AWS EC2 Instance Connect for EC2 instances
      return `aws ec2-instance-connect ssh --instance-id ${vm.id} --region ${vm.region} --os-user ubuntu`;
    }
  } else {
    // GCP SSH 命令
    const baseCmd = `gcloud compute ssh ${vm.name} --zone ${vm.zone} --project ${props.currentProjectId}`;
    if (hasExternalIP.value) {
      return baseCmd;
    } else {
      return `${baseCmd} --tunnel-through-iap`;
    }
  }
};

// 判断是否有外部IP
const hasExternalIP = (vm) => {
  const externalIP = getExternalIP(vm);
  return externalIP && externalIP !== t('vm.na');
};

// 复制SSH命令
const copySshCommand = async (vm) => {
  try {
    const sshCommand = getSshCommand(vm);
    await navigator.clipboard.writeText(sshCommand);
    ElMessage.success(t('common.sshCommandCopied'));
  } catch (error) {
    console.error('复制失败:', error);
    ElMessage.error(t('common.copyFailed'));
  }
};

// 检查资源是否有标签
const hasLabels = (resource) => {
  return resource.labels && (
    resource.labels.owner ||
    resource.labels.team ||
    resource.labels.org
  );
};

// 获取团队显示名称
const getTeamDisplayName = (team) => {
  const teamNames = {
    'si-dev': t('team.siDev'),
    'algo-dev': t('team.algoDev'),
    'infra-dev': t('team.infraDev'),
    'hi': t('team.hi')
  };
  return teamNames[team] || team;
};

// 获取组织显示名称
const getOrgDisplayName = (org) => {
  const orgNames = {
    'eng': t('org.engineering'),
    'product': t('org.product')
  };
  return orgNames[org] || org;
};

// 格式化owner显示
const formatOwnerForDisplay = (owner) => {
  if (!owner) {return '';}
  let email = owner.replace(/_/g, '.');
  if (!email.endsWith('@robotruck.jp')) {
    email += '@robotruck.jp';
  }
  return email;
};

// 打开标签编辑模态框
const openLabelModal = (resourceType, resource) => {
  labelModalData.value = {
    resourceType,
    resourceName: resource.name,
    zone: resource.zone,
    currentLabels: resource.labels || {}
  };
  labelModalVisible.value = true;
};

// 关闭标签编辑模态框
const closeLabelModal = () => {
  labelModalVisible.value = false;
};

// 处理标签更新
const handleLabelsUpdated = (updatedLabels) => {
  emit('labels-updated', updatedLabels);
};

const props = defineProps({
  resources: Object,
  user: Object,
  currentProjectId: String,
  isOperating: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits(['vm-operation', 'delete-disk', 'resource-click', 'labels-updated']);

</script>

<style scoped>
.list-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.resource-tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.tab-btn {
  padding: 12px 24px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
}

.tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
}

.resource-list {
  flex: 1;
  overflow-y: auto;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #666;
  text-align: center;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state p {
  margin: 8px 0;
  font-size: 16px;
}

.resource-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #eee;
  transition: all 0.3s ease;
}

.resource-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.card-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.status-running {
  background: #e8f5e8;
  color: #2e7d32;
}

.status-stopped {
  background: #ffebee;
  color: #c62828;
}

.status-terminated {
  background: #f5f5f5;
  color: #666;
}

.status-ready {
  background: #e8f5e8;
  color: #2e7d32;
}

.status-creating {
  background: #fff3e0;
  color: #ef6c00;
}

.status-restoring {
  background: #e3f2fd;
  color: #1565c0;
}

.card-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  position: relative;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;
  color: white;
  background: #2196F3;
  margin-left: 0px;
}
.action-btn.info-btn {
  background: #2196F3;
}
.action-btn.info-btn:hover {
  background: #1769aa;
}
.popover-detail-card {
  font-size: 1rem;
  color: #333;
  min-width: 220px;
  max-width: 320px;
  line-height: 1.7;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(80,80,80,0.10);
  padding: 1rem 1.2rem;
}
.popover-title {
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #396afc;
}

::v-deep .el-popover__wrapper {
  z-index: 2000 !important;
}

.ssh-section {
  grid-column: 1 / -1;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.ssh-command-wrapper {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.ssh-command-input {
  width: 100%;
}

.ssh-note {
  font-size: 0.8rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.ssh-note i {
  color: #409eff;
}

/* 标签样式 */
.labels-section {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 4px;
}

.labels-display {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.no-labels {
  flex: 1;
  display: flex;
  align-items: center;
}

.no-labels-text {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.label-edit-btn {
  padding: 4px 8px;
  font-size: 11px;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.label-edit-btn:hover {
  background: #f5f5f5;
  border-color: #ccc;
  color: #333;
}

.label-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.label-tag.owner {
  background: #d4edda;
  color: #155724;
}

.label-tag.team {
  background: #d1ecf1;
  color: #0c5460;
}

.label-tag.org {
  background: #fff3cd;
  color: #856404;
}

.label-tag i {
  font-size: 9px;
}

/* 挂载信息样式 */
.detail-item .status-unattached {
  color: #999;
  font-style: italic;
}

.detail-item .detail-value i {
  margin-right: 5px;
  color: #666;
}

.detail-item .detail-value i.fa-link {
  color: #28a745;
}

/* On-premise card styles */
.resource-card.on-premise {
  border-left: 4px solid #9c27b0;
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.resource-card.on-premise .card-title {
  color: #6a1b9a;
}

.vm-icon {
  margin-right: 8px;
  color: #666;
}

.on-premise-badge {
  background: #9c27b0;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  margin-left: 8px;
}

.detail-item .detail-value i.fa-hdd {
  color: #007bff;
}

.detail-item .detail-value i.fa-unlink {
  color: #dc3545;
}
</style>
